import { useEffect, useState } from "react";
import { Users, Wallet, ScanLine, Clock } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { PageHeader, Panel, StatCard } from "@/components/PageHeader";
import { EventPicker, useStatsEvents } from "@/components/EventPicker";
import { api, cedi } from "@/lib/api";

const PIE = ["#E60000", "#111111", "#6B7280", "#F87171", "#D1D5DB", "#991B1B"];

const Trend = ({ data }) => (
  <ResponsiveContainer width="100%" height={260}>
    <AreaChart data={data} margin={{ left: -20, right: 8 }}>
      <defs><linearGradient id="red" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E60000" stopOpacity={0.25} /><stop offset="100%" stopColor="#E60000" stopOpacity={0} /></linearGradient></defs>
      <CartesianGrid stroke="#F0F0F0" vertical={false} />
      <XAxis dataKey="day" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
      <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
      <Tooltip />
      <Area type="monotone" dataKey="registrations" name="Registrations" stroke="#E60000" strokeWidth={2.5} fill="url(#red)" />
      <Area type="monotone" dataKey="checkins" name="Check-ins" stroke="#111111" strokeWidth={2} fillOpacity={0} />
    </AreaChart>
  </ResponsiveContainer>
);

const Regions = ({ data }) => (
  <ResponsiveContainer width="100%" height={260}>
    <BarChart data={data} layout="vertical" margin={{ left: 20 }}>
      <XAxis type="number" hide allowDecimals={false} />
      <YAxis type="category" dataKey="region" tick={{ fontSize: 11 }} width={95} axisLine={false} tickLine={false} />
      <Tooltip />
      <Bar dataKey="count" fill="#111111" radius={[0, 4, 4, 0]} barSize={14} />
    </BarChart>
  </ResponsiveContainer>
);

const Methods = ({ data }) => (
  <div className="flex flex-col sm:flex-row items-center gap-4">
    <ResponsiveContainer width={180} height={180}>
      <PieChart><Pie data={data} dataKey="amount" nameKey="method" innerRadius={50} outerRadius={80} paddingAngle={2}>{data.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}</Pie><Tooltip formatter={(v) => cedi(v)} /></PieChart>
    </ResponsiveContainer>
    <ul className="flex-1 space-y-2 w-full">
      {data.map((m, i) => (
        <li key={m.method} className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE[i % PIE.length] }} />{m.method}</span>
          <span className="font-mono text-xs">{cedi(m.amount)}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Empty = ({ testId, children }) => <div className="h-[180px] grid place-items-center text-sm text-gray-500" data-testid={testId}>{children}</div>;

const Charts = ({ s }) => (
  <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-4">
    <Panel title="Registrations & Check-ins per Day" className="xl:col-span-2" testId="chart-trend">{s.trend.length ? <Trend data={s.trend} /> : <Empty testId="chart-trend-empty">No registrations yet</Empty>}</Panel>
    <Panel title="Participants by Region" testId="chart-regions">{s.regions.length ? <Regions data={s.regions} /> : <Empty testId="chart-regions-empty">No registrations yet</Empty>}</Panel>
    <Panel title="Revenue by Payment Channel" className="xl:col-span-3" testId="chart-methods">{s.methods.length ? <Methods data={s.methods} /> : <Empty testId="chart-methods-empty">No payments recorded yet</Empty>}</Panel>
  </div>
);

export default function Dashboard() {
  const [events, eventId, setEventId] = useStatsEvents();
  const [s, setS] = useState(null);
  useEffect(() => {
    setS(null);
    if (eventId) { api.get("/stats", { params: { event_id: eventId } }).then((r) => setS(r.data)).catch(() => setS({ error: true })); }
  }, [eventId]);
  const header = <PageHeader eyebrow="Executive Overview" title="Analytics Dashboard" description="Live registrations, revenue and gate activity for the selected event."
    actions={events?.length ? <EventPicker events={events} value={eventId} onChange={setEventId} testId="dashboard-event-picker" /> : null} />;
  if (events && !events.length) return <div data-testid="dashboard-page">{header}<Empty testId="dashboard-no-events">No events yet. Create an event to see its analytics here.</Empty></div>;
  if (!s) return <div data-testid="dashboard-page">{header}<div className="text-gray-500" data-testid="dashboard-loading">Loading analytics…</div></div>;
  if (s.error) return <div data-testid="dashboard-page">{header}<Empty testId="dashboard-error">Could not load analytics. Please refresh the page.</Empty></div>;
  const pct = s.registered ? Math.round((s.checked_in / s.registered) * 100) : 0;
  return (
    <div data-testid="dashboard-page">
      {header}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard label="Registered Participants" value={s.registered} sub={`${s.kits} kits issued`} icon={Users} testId="kpi-registered" />
        <StatCard label="Revenue Collected" value={cedi(s.revenue)} sub={`${s.payments} payment${s.payments === 1 ? "" : "s"}`} icon={Wallet} testId="kpi-revenue" delay={60} />
        <StatCard label="Gate Check-Ins" value={`${s.checked_in} / ${s.registered}`} sub={`${pct}% checked in`} icon={ScanLine} testId="kpi-checkins" delay={120} />
        <StatCard label="Outstanding Balances" value={cedi(s.pending_amount)} sub="Still to be paid" icon={Clock} testId="kpi-pending" delay={180} />
      </div>
      <Charts s={s} />
    </div>
  );
}
